import clsx from "clsx";
import { useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { Address, isThor } from "rujira.js";
import {
  useGlobalModalContext,
  type ModalProps,
} from "../../context/GlobalModal";
import { convertByte32HexString, sha256, sleep } from "../../helpers";
import { useTranslation } from "../../i18n";
import { PrivacyProvider } from "../../privacy";
import { AccountProvider } from "../../wallets";
import PrivateModeOnboarding from "./PrivateModeOnboarding";
import { PrivacyGhost, useHeaderGhostMotion } from "./PrivacyGhost";
import UnlockPrivateMode from "./UnlockPrivateMode";
import { PrivateModeToggleV2 } from "./PrivateModeToggleV2";

export type PrivateModeToggleProps = {
  accountProvider: AccountProvider;
  isPrivateMode?: boolean;
  setIsPrivateMode?: (isPrivateMode: boolean) => void;
  setStealth?: (stealth: string) => void;
  stealth?: string | null;
  subWalletAddress?: string | null;
  setSubWalletAddress?: (subWalletAddress: string) => void;
  setPublicAddress?: (publicAddress: string) => void;
  privacyProvider?: PrivacyProvider;
  encryptionSignature?: string | null;
  setEncryptionSignature?: (encryptionSignature: string) => void;
};

/**
 * Privacy-mode toggle for the Header, owning the full private mode
 * onboarding / unlock / restore flows. All backend and signing concerns
 * are delegated to the injected PrivacyProvider.
 */
const PrivateModeToggleV1 = ({
  accountProvider,
  isPrivateMode,
  setIsPrivateMode,
  stealth,
  setStealth,
  subWalletAddress,
  setSubWalletAddress,
  setPublicAddress,
  privacyProvider,
  encryptionSignature,
  setEncryptionSignature,
}: PrivateModeToggleProps) => {
  const { t: t_redacted } = useTranslation("redacted");
  const { accounts, selected } = accountProvider;
  const { showModal: show, hideModal } = useGlobalModalContext();
  // Onboarding and unlock set the private account themselves.
  const showModal = (modal: ModalProps) =>
    show({ ...modal, keepOnAccountChange: true });

  const checkIsRegisteredPrivateAccount = useCallback(
    async (address: string) => {
      if (!privacyProvider) return null;
      return privacyProvider.isRegisteredPrivateAccount(address);
    },
    [privacyProvider]
  );
  // Helper function to fetch stealth with signature authentication
  const fetchStealthWithSignature = useCallback(
    async (address: string) => {
      try {
        if (!selected || !privacyProvider) return null;
        const message = `${address.toLowerCase()}-get-stealth`;
        const signature = await privacyProvider.signMessage(address, message);
        if (!signature) {
          console.error("---Failed to generate signature");
          return null;
        }
        return await privacyProvider.getStealth(address, signature);
      } catch (error) {
        console.error("Cancelled approve:", error);
        return null;
      }
    },
    [privacyProvider, selected]
  );

  const getEncryptionSignature = useCallback(
    async (address: Address) => {
      if (!selected || !isThor(address)) {
        toast.error("No wallet or Thor address. Connect wallet");
        return null;
      }
      if (!privacyProvider) {
        toast.error("No wallet signer. Connect wallet");
        return null;
      }
      const message = `${address.address.toLowerCase()}-redacted-derive-secret`;
      try {
        const signature = await privacyProvider.signMessage(
          address.address,
          message
        );
        if (!signature) {
          console.error("---Failed to generate signature");
          return null;
        }
        return signature.signature;
      } catch {
        return null;
      }
    },
    [privacyProvider, selected]
  );

  // An active V1 toggle must always represent a complete, in-memory proof
  // session. Credentials are intentionally not persisted, so a reload must
  // fail closed and require a fresh wallet unlock.
  useEffect(() => {
    const restorePrivateMode = async () => {
      if (!isPrivateMode) return;
      if (!selected || !isThor(selected.address)) return;
      if (stealth && subWalletAddress && encryptionSignature) return;
      setIsPrivateMode?.(false);
      setPrivateMode();
    };

    restorePrivateMode();
  }, [isPrivateMode, selected, stealth, subWalletAddress, encryptionSignature]);
  const setPrivateMode = async () => {
    if (!selected || !isThor(selected.address)) {
      toast.error("No wallet or Thor address. Connect wallet");
      return;
    }
    setPublicAddress!(selected.address.address);
    if (stealth && subWalletAddress && encryptionSignature) {
      setIsPrivateMode!(true);
      return;
    }
    const data = await checkIsRegisteredPrivateAccount(
      selected.address.address
    );
    if (!data) {
      toast.error(
        "The private account service is unavailable. Please try again."
      );
      return;
    }
    if (!data.is_registered) {
      showModal({
        title: "",
        backgroundClose: true,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            currentStep={1}
            onSign={() => {
              onPrivateModeSign(1, {});
            }}
            isSigning={false}
            completedSteps={[]}
          />
        ),
      });
    } else if (!data.is_created) {
      // registered but not created subwallet
      showModal({
        title: "",
        backgroundClose: true,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={1}
            onSign={() => {
              onPrivateModeSign(1, {});
            }}
            isSigning={false}
            completedSteps={[]}
          />
        ),
      });
    } else {
      showModal({
        title: "",
        backgroundClose: true,
        className: "modal--unlock-private",
        children: (
          <UnlockPrivateMode
            onUnlock={() => {
              onPrivateModeUnlock();
            }}
          />
        ),
      });
    }
  };
  const onPrivateModeUnlock = async () => {
    showModal({
      title: "",
      backgroundClose: false,
      className: "modal--unlock-private",
      children: <UnlockPrivateMode isSigning={true} />,
    });

    if (!selected || !isThor(selected.address)) {
      hideModal();
      toast.error("No wallet or Thor address. Connect wallet");
      return;
    }
    setPublicAddress!(selected.address.address);
    const data = await fetchStealthWithSignature(selected.address.address);
    if (data) {
      const newStealth = convertByte32HexString(data.stealth as string);
      setStealth!(newStealth);
      if (data.sub_wallet) {
        setSubWalletAddress!(data.sub_wallet as string);
      }
      await sleep(1000);
      const encryptionSignature = await getEncryptionSignature(
        selected.address
      );
      if (!encryptionSignature) {
        hideModal();
        return;
      }
      setEncryptionSignature!(encryptionSignature);
      setIsPrivateMode!(true);
    } else {
      hideModal();
      toast.error("Private Mode could not be unlocked. Please try again.");
      return;
    }
    showModal({
      title: "",
      backgroundClose: false,
      className: "modal--unlock-private",
      children: <UnlockPrivateMode isSuccess={true} isSigning={false} />,
    });
    setTimeout(() => {
      hideModal();
    }, 1500);
  };
  const onPrivateModeSign = async (
    currentStep: number,
    options: {
      stealthParam?: string;
      encryptionKey?: string;
    }
  ) => {
    if (currentStep == 1) {
      if (!selected || !isThor(selected.address)) {
        toast.error("No wallet or Thor address. Connect wallet");
        hideModal();
        return;
      }
      if (!privacyProvider) {
        toast.error("No wallet signer. Connect wallet");
        hideModal();
        return;
      }
      //showing signing
      showModal({
        title: "",
        backgroundClose: false,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={1}
            isSigning={true}
            completedSteps={[]}
          />
        ),
      });

      const deriveMsg = `${selected.address.address.toLowerCase()}-redacted-derive-secret`;
      const deriveSig = await privacyProvider.signMessage(
        selected.address.address,
        deriveMsg
      );
      if (!deriveSig) {
        showModal({
          title: "",
          backgroundClose: false,
          className: "modal--privacy-mode",
          children: (
            <PrivateModeOnboarding
              onClose={() => {
                hideModal();
              }}
              currentStep={1}
              onSign={() => {
                onPrivateModeSign(1, {});
              }}
              isSigning={false}
              completedSteps={[]}
            />
          ),
        });
        return;
      }
      setEncryptionSignature!(deriveSig.signature);
      showModal({
        title: "",
        backgroundClose: true,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={2}
            onSign={() => {
              onPrivateModeSign(2, {
                encryptionKey: deriveSig.signature,
              });
            }}
            isSigning={false}
            completedSteps={[1]}
          />
        ),
      });
    } else if (currentStep == 2) {
      // register account
      if (!selected || !isThor(selected.address)) {
        toast.error("No wallet or Thor address. Connect wallet");
        hideModal();
        return;
      }
      if (!privacyProvider) {
        toast.error("No wallet signer. Connect wallet");
        hideModal();
        return;
      }
      //showing signing
      showModal({
        title: "",
        backgroundClose: false,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={2}
            isSigning={true}
            completedSteps={[1]}
          />
        ),
      });
      const encryptionKey = options.encryptionKey;
      if (!encryptionKey) {
        toast.error("No encryption key.");
        hideModal();
        return;
      }
      const resRegister = await privacyProvider.register({
        address: selected.address.address,
        derivationSignature: encryptionKey,
      });
      if (!resRegister.ok) {
        toast.error(`Failed to register account: ${resRegister.err}`);
        hideModal();
        return;
      }
      let newStealth = convertByte32HexString(resRegister.stealth as string);
      setStealth!(newStealth);

      showModal({
        title: "",
        backgroundClose: true,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={3}
            onSign={() => {
              onPrivateModeSign(3, {
                stealthParam: newStealth,
              });
            }}
            isSigning={false}
            completedSteps={[1, 2]}
          />
        ),
      });
    } else if (currentStep == 3) {
      // create Private Account
      if (!selected || !isThor(selected.address)) {
        toast.error("No wallet or Thor address. Connect wallet");
        hideModal();
        return;
      }
      if (!privacyProvider) {
        toast.error("Couldn't generate proof.");
        hideModal();
        return;
      }
      // Use stealthParam if provided (from step 1), otherwise fall back to state
      const stealthToUse = options?.stealthParam || stealth;
      if (!stealthToUse) {
        toast.error("Stealth is null.");
        hideModal();
        return;
      }
      showModal({
        title: "",
        backgroundClose: false,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={3}
            isSigning={true}
            completedSteps={[1, 2]}
          />
        ),
      });

      let nonce = await privacyProvider.getNonce(stealthToUse);
      if (!nonce) {
        nonce = "0";
      }
      const param = await sha256(nonce);
      const proofData = await privacyProvider.generateProof(
        param,
        false,
        selected.address.address,
        stealthToUse
      );
      if (!proofData || !proofData.proof) {
        toast.error("Failed to get proof data. please try again.");
        showModal({
          title: "",
          backgroundClose: false,
          className: "modal--privacy-mode",
          children: (
            <PrivateModeOnboarding
              onClose={() => {
                hideModal();
              }}
              currentStep={3}
              onSign={() => {
                onPrivateModeSign(3, { stealthParam: stealthToUse });
              }}
              isSigning={false}
              completedSteps={[1, 2]}
            />
          ),
        });
        return;
      }
      // Create the sub-wallet with proof via the privacy provider
      const newSubWallet = await privacyProvider.createSubWallet({
        proof: proofData.proof,
        param,
        address: selected.address.address,
        stealth: stealthToUse,
        signature: proofData.signature,
      });
      if (newSubWallet) {
        setSubWalletAddress!(newSubWallet as string);
      } else {
        toast.error("Failed to create subWallet. please try again.");
        showModal({
          title: "",
          backgroundClose: true,
          className: "modal--privacy-mode",
          children: (
            <PrivateModeOnboarding
              onClose={() => {
                hideModal();
              }}
              currentStep={3}
              onSign={() => {
                onPrivateModeSign(3, { stealthParam: stealthToUse });
              }}
              isSigning={false}
              completedSteps={[1, 2]}
            />
          ),
        });
        return;
      }
      setIsPrivateMode!(true);
      showModal({
        title: "",
        backgroundClose: true,
        className: "modal--privacy-mode",
        children: (
          <PrivateModeOnboarding
            onClose={() => {
              hideModal();
            }}
            currentStep={3}
            onSign={() => {
              onPrivateModeSign(3, { stealthParam: stealthToUse });
            }}
            isSigning={false}
            completedSteps={[1, 2, 3]}
          />
        ),
      });
    }
  };

  const ghost = useHeaderGhostMotion(!!isPrivateMode);
  if (accounts?.length === 0) return null;

  return (
    <>
      <button
        className={clsx("transparent rujira-header__private condensed", {
          "rujira-header__private--active": isPrivateMode,
        })}
        aria-label="Private Mode"
        data-tooltip-id="private-mode-tip"
        data-tooltip-content={
          isPrivateMode
            ? t_redacted("privacyModeEnabled")
            : t_redacted("privacyModeDisabled")
        }
        onMouseOver={() => ghost.setHover(true)}
        onMouseOut={() => ghost.setHover(false)}
        onClick={() => {
          if (isPrivateMode) {
            setIsPrivateMode!(false);
          } else {
            setPrivateMode();
          }
        }}>
        <PrivacyGhost motion={ghost.motion} />
      </button>
      <Tooltip id="private-mode-tip" className="tooltip" />
    </>
  );
};

export const PrivateModeToggle = (props: PrivateModeToggleProps) =>
  props.privacyProvider?.version === 2 && props.privacyProvider.v2 ? (
    <PrivateModeToggleV2
      controls={props.privacyProvider.v2}
      isPrivateMode={props.isPrivateMode}
      hasAccounts={!!props.accountProvider.accounts?.length}
    />
  ) : (
    <PrivateModeToggleV1 {...props} />
  );
